import { Helmet } from 'react-helmet-async';
import { seoConfig } from '@/config/seo';

interface SEOHeadProps {
  title?: string;
  description?: string;
  path?: string;
  image?: string;
  noindex?: boolean;
}

export default function SEOHead({ 
  title,
  description,
  path = '',
  image,
  noindex = false
}: SEOHeadProps) {
  const pageTitle = title 
    ? `${title} | ${seoConfig.siteName}`
    : seoConfig.title;
    
  const pageDescription = description || seoConfig.description;
  const pageUrl = `${seoConfig.siteUrl}${path}`;
  const pageImage = `${seoConfig.siteUrl}${image || seoConfig.og.image}`;
  
  return (
    <Helmet>
      {/* Basic Meta Tags */}
      <title>{pageTitle}</title>
      <meta name="description" content={pageDescription} />
      <meta name="keywords" content={seoConfig.keywords.join(', ')} />
      <meta name="author" content={seoConfig.author} />
      <link rel="canonical" href={pageUrl} />
      
      {/* Favicon */}
      <link rel="icon" type="image/svg+xml" href="/favicon.svg" />
      <link rel="alternate icon" href="/favicon.ico" />
      <link rel="apple-touch-icon" href="/apple-touch-icon.png" />
      
      {/* Open Graph */}
      <meta property="og:title" content={pageTitle} />
      <meta property="og:description" content={pageDescription} />
      <meta property="og:type" content={seoConfig.og.type} />
      <meta property="og:url" content={pageUrl} />
      <meta property="og:site_name" content={seoConfig.siteName} />
      <meta property="og:image" content={pageImage} />
      <meta property="og:image:width" content={String(seoConfig.og.imageWidth)} />
      <meta property="og:image:height" content={String(seoConfig.og.imageHeight)} />
      <meta property="og:locale" content={seoConfig.og.locale} />
      
      {/* Twitter Card */}
      <meta name="twitter:card" content={seoConfig.twitter.cardType} />
      <meta name="twitter:site" content={seoConfig.twitter.site} />
      <meta name="twitter:creator" content={seoConfig.twitter.handle} />
      <meta name="twitter:title" content={pageTitle} />
      <meta name="twitter:description" content={pageDescription} />
      <meta name="twitter:image" content={pageImage} />
      
      {/* Additional Meta Tags */}
      <meta name="viewport" content="width=device-width, initial-scale=1.0" />
      <meta httpEquiv="Content-Type" content="text/html; charset=utf-8" />
      <meta name="theme-color" content="#000000" />
      
      {/* Robots */}
      {noindex && <meta name="robots" content="noindex, nofollow" />}
      
      {/* Structured Data */}
      <script type="application/ld+json">
        {JSON.stringify(seoConfig.structuredData)}
      </script>
    </Helmet>
  );
}